import { useEffect, useId, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';

import useIsPWA from '@/hooks/use-is-pwa';
import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface TextProps<T extends FieldValues = FieldValues> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
    label?: ReactNode;
    value?: string | number | null;
    error?: FieldErrorLike;
    /** Transforme la saisie avant qu'elle n'atteigne `onChange` (majuscules, format IBAN…). */
    normalize?: (value: string) => string;
    /** Rappel métier après chaque saisie, avec le `setValue` du formulaire. */
    functionOnChange?: (value: string, setValue?: UseFormSetValue<T>) => void;
    setValue?: UseFormSetValue<T>;
}

/** Champ texte à libellé flottant. */
const Text = <T extends FieldValues = FieldValues>(props: TextProps<T>) => {
    const { label, value, error, required, normalize = (v: string) => v, onChange, functionOnChange, setValue, ...other } = props;
    const id = useId();
    const errorId = `${id}-error`;
    const [focused, setFocused] = useState(false);
    const isPWA = useIsPWA();
    const labelRef = useRef<HTMLLabelElement>(null);
    const [isMultiLine, setIsMultiLine] = useState(false);

    const isActive = focused || value;

    useEffect(() => {
        if (isPWA && labelRef.current && isActive) {
            setIsMultiLine(labelRef.current.offsetHeight > 20);
        }
    }, [isPWA, label, isActive]);

    return (
        <div className="relative w-full">
            <input
                id={id}
                required={required}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                value={value ?? undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    e.target.value = normalize(e.target.value);
                    if (functionOnChange) functionOnChange(e.target.value, setValue);
                    onChange?.(e);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className={`w-full px-6 ${isPWA && isMultiLine && isActive ? 'pt-6 pb-2' : 'py-4'} rounded-lg border border-border bg-transparent text-base transition-colors placeholder-transparent focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-muted disabled:text-muted-foreground ${error ? 'border-destructive focus:ring-destructive/20 focus:border-destructive' : ''}`}
                placeholder={typeof label === 'string' ? label : undefined}
                onWheel={(e) => e.currentTarget.blur()}
                {...other}
            />
            <label
                ref={labelRef}
                htmlFor={id}
                className={`absolute left-3 ${isPWA ? (isActive ? 'bg-white' : 'bg-transparent') : 'bg-card'} px-1 text-muted-foreground transition-all duration-200 pointer-events-none ${isActive ? 'text-xs -top-2 text-primary' : 'text-sm top-4'} ${error && isActive ? 'text-destructive' : ''}`}
            >
                {label}
                {required ? (
                    <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                        *
                    </sup>
                ) : (
                    ''
                )}
            </label>
            {error && (
                <p id={errorId} role="alert" aria-live="assertive" className="text-xs text-destructive italic mt-1">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default Text;
